"use client";

import { useEffect, useMemo, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import IPTypeBadge from "@/components/ui/IPTypeBadge";
import StatusBadge from "@/components/ui/StatusBadge";
import { apiFetch } from "@/lib/api/client";
import type { DashboardSummary } from "../types";

type Props = { selectedDate: string };

const priorityConfig = {
  high: { label: "High", className: "bg-red-50 text-red-700 border-red-200" },
  medium: { label: "Medium", className: "bg-amber-50 text-amber-700 border-amber-200" },
  low: { label: "Low", className: "bg-green-50 text-green-700 border-green-200" },
};

function daysLeftColor(days: number) {
  if (days <= 5) return "text-red-600 font-700";
  if (days <= 14) return "text-amber-600 font-600";
  return "text-foreground font-500";
}

export default function UpcomingDeadlinesTable({ selectedDate }: Props) {
  const [data, setData] = useState<DashboardSummary['upcomingDeadlines'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState<string>("daysLeft");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const queryParams = selectedDate ? `?date=${selectedDate}` : "";
        const res = await apiFetch<DashboardSummary['upcomingDeadlines']>(`/api/v1/dashboard/deadlines${queryParams}`, { method: "GET", auth: true });
        if (active) setData(res);
      } catch (error) {
        console.error("Failed to load upcoming deadlines data:", error);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [selectedDate]);

  const rows = data ?? [];

  const sorted = useMemo(
    () => [...rows].sort((a, b) => {
      const av = (a as any)[sortCol];
      const bv = (b as any)[sortCol];
      if (typeof av === "number") return sortDir === "asc" ? av - bv : bv - av;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    }),
    [rows, sortCol, sortDir]
  );

  const SortIcon = ({ col }: { col: string }) => (
    <Icon name={sortCol === col ? (sortDir === "asc" ? "ChevronUpIcon" : "ChevronDownIcon") : "ChevronUpDownIcon"} size={12} className={sortCol === col ? "text-primary" : "text-muted-foreground"} />
  );

  if (loading || !data) return <div className="bg-white rounded-xl border border-border shadow-card p-5 h-[420px] animate-pulse" />;

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[15px] font-600 text-foreground">Upcoming Deadlines</h3>
          <p className="text-[12px] text-muted-foreground mt-0.5">Next 30 days across all IP types</p>
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin -mx-1">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-border">
              {[
                { key: "caseNo", label: "Case No." },
                { key: "title", label: "Case Title" },
                { key: "ipType", label: "Type" },
                { key: "taskType", label: "Task" },
                { key: "client", label: "Client" },
                { key: "daysLeft", label: "Due" },
                { key: "priority", label: "Priority" },
                { key: "assignedTo", label: "Assigned" },
                { key: "status", label: "Status" },
              ].map((col) => (
                <th key={col.key} onClick={() => {
                  if (sortCol === col.key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                  else { setSortCol(col.key); setSortDir("asc"); }
                }} className="text-left py-2 px-2 text-[11px] font-600 text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors select-none whitespace-nowrap">
                  <span className="flex items-center gap-1">{col.label}<SortIcon col={col.key} /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-10 text-center">
                  <div className="flex flex-col items-center justify-center text-muted-foreground">
                    <Icon name="CalendarIcon" size={32} className="mb-3 opacity-30" />
                    <p className="text-[13px] font-500 text-foreground">No upcoming deadlines</p>
                    <p className="text-[12px] mt-1">You're all caught up for the next 30 days!</p>
                  </div>
                </td>
              </tr>
            ) : (
              sorted.map((row, idx) => (
                <tr key={row.id} className={`border-b border-border/60 hover:bg-muted/40 transition-colors duration-100 ${idx % 2 === 0 ? "" : "bg-muted/10"}`}>
                  <td className="py-2.5 px-2"><span className="font-mono text-[11px] font-600 text-primary whitespace-nowrap">{row.caseNo}</span></td>
                  <td className="py-2.5 px-2"><p className="text-[13px] font-500 text-foreground truncate max-w-[180px]" title={row.title}>{row.title}</p></td>
                  <td className="py-2.5 px-2"><IPTypeBadge type={row.ipType} size="sm" /></td>
                  <td className="py-2.5 px-2"><span className="text-[12px] text-foreground whitespace-nowrap">{row.taskType}</span></td>
                  <td className="py-2.5 px-2"><p className="text-[12px] text-muted-foreground truncate max-w-[130px]" title={row.client}>{row.client}</p></td>
                  <td className="py-2.5 px-2"><div><span className={`text-[13px] tabular-nums ${daysLeftColor(row.daysLeft)}`}>{row.daysLeft}d</span><p className="text-[10px] text-muted-foreground whitespace-nowrap">{row.dueDate}</p></div></td>
                  <td className="py-2.5 px-2"><span className={`text-[10px] font-600 px-2 py-0.5 rounded-full border ${priorityConfig[row.priority].className}`}>{priorityConfig[row.priority].label}</span></td>
                  <td className="py-2.5 px-2"><span className={`text-[12px] ${row.assignedTo === "Unassigned" ? "text-red-500 font-600" : "text-muted-foreground"}`}>{row.assignedTo}</span></td>
                  <td className="py-2.5 px-2"><StatusBadge status={row.status} size="sm" /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
